import React, { useEffect, useMemo, useState } from "react"
import { Image as RNImage } from "~/ui/primitives"
import FastImage from "~/ui/FastImage"
import * as VideoThumbnails from "~/ui/expo-video-thumbnails"
import useNavigation from "~hooks/useNavigation"
import Progress from "./progress"
import { Loading } from "~widgets/loading"
import { Message, useStores } from "../../models"
import { getMediaPath, isLocalMediaPath } from "../../utils"
import { MessageTypes } from "~proto"
import { buildGalleryImageKey } from "~utils/gallery-image"
import { fitContainSize, getChatMediaMaxBounds, CHAT_IMAGE_MAX_WIDTH } from "~utils/media-fit"
import styled from '~/ui/styled'
import { MessageStatus } from "~proto"
import { hapticTrigger } from "../../utils/haptic"
import { observer } from "mobx-react-lite"
import MessageSent from "./message-sent"
import MessageReceived from "./message-received"
import MessageTime from "./message-time"
import { MessageProps } from "./message.props"
import { Flex } from "~components/El"

const placeholder = require("../../assets/images/placeholder.jpg")

const Container = styled.div`
  width: fit-content;
  max-width: ${CHAT_IMAGE_MAX_WIDTH}px;
`

const VideoContainer = styled.div`
  width: fit-content;
  max-width: ${CHAT_IMAGE_MAX_WIDTH}px;
  margin: 2px;
  border-radius: 6px;
  overflow: hidden;
`

const VideoMessageTouchableWrap = styled(Flex)`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`

const TimeWrap = styled.View<{ byMe: boolean }>`
  width: 100%;
  align-items: ${({ byMe }) => (byMe ? "flex-end" : "flex-start")};
  margin-top: 4px;
  padding-left: 2px;
  padding-right: 2px;
`

const VideoPoster = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
`

const PlayButton = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: 2px solid rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  flex-shrink: 0;
  pointer-events: none;

  &::after {
    content: "";
    display: block;
    width: 0;
    height: 0;
    margin-left: 4px;
    border-style: solid;
    border-width: 9px 0 9px 15px;
    border-color: transparent transparent transparent #fff;
  }
`

const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  cursor: pointer;
`

interface VideoMessageProps {
  message: Message
  messageHack?: Partial<Message>
  setPopupOptionListModal?: ({ visible, message }: { visible: boolean; message: Message }) => void
}

// 优化使用纯组件
export const VideoMessage: React.FC<VideoMessageProps> = observer(
  ({ message: pMessage, messageHack, setPopupOptionListModal }) => {
    const message = {
      ...pMessage,
      ...(messageHack || {}),
    }
    const video = getMediaPath(message)
    const navigation = useNavigation()
    const { messageStore } = useStores()

    const m = messageStore.messages.get(message.message_id)
    const previewImage =
      message.attach?.localVideoPreviewImageUri || message.attach?.previewUri
    const [loading, setLoading] = useState<boolean>(false)
    const [progress, setProgress] = useState<number>(0)
    const [intrinsic, setIntrinsic] = useState<{ w: number; h: number } | null>(null)
    const [localThumb, setLocalThumb] = useState<string>("")
    const [useVideoPoster, setUseVideoPoster] = useState(false)

    const { maxWidth: boundsMaxW, maxHeight: maxH } = getChatMediaMaxBounds()
    const maxW = Math.min(boundsMaxW, CHAT_IMAGE_MAX_WIDTH)

    const { displayW, displayH } = useMemo(() => {
      if (!intrinsic || intrinsic.w <= 0 || intrinsic.h <= 0) {
        return { displayW: maxW, displayH: maxW }
      }
      return fitContainSize(intrinsic.w, intrinsic.h, maxW, maxH)
    }, [intrinsic, maxW, maxH])

    const displayPreview = previewImage || localThumb

    useEffect(() => {
      // previewImage 随 message 变化（含 recycleItems 复用），重置尺寸/加载状态，避免串台
      setIntrinsic(null)
      setLoading(false)
      setProgress(0)
      setLocalThumb("")
      setUseVideoPoster(false)
      if (!displayPreview) return undefined
      // FastImage 在 Android 上 onLoad 不会回传 width/height，
      // 改用 RN 内置 Image.getSize 读取缩略图原始宽高。
      let cancelled = false
      RNImage.getSize(
        displayPreview,
        (w, h) => {
          if (!cancelled && w > 0 && h > 0) {
            setIntrinsic({ w, h })
          }
        },
        () => {
          // 取不到尺寸时退化为默认占位尺寸
        },
      )
      return () => {
        cancelled = true
      }
    }, [displayPreview, video, message.message_id])

    // 下载进度条提示
    const onProgress = ({ nativeEvent: { loaded, total } }) => {
      setProgress(loaded / total)
    }

    // 加载图片失败的处理, TODO
    const onError = () => {
      //
    }

    // 开始加载图片
    const onLoadStart = () => {
      setLoading(true)
    }

    // 加载结束， 不管是否成功或失败
    const onLoadEnd = () => {
      setLoading(false)
    }

    const generateThumbnail = async () => {
      if (!video) return
      if (!isLocalMediaPath(video)) {
        setUseVideoPoster(true)
        return
      }
      try {
        const { uri } = await VideoThumbnails.getThumbnailAsync({ uri: video }, { time: 100 })
        if (!uri) {
          setUseVideoPoster(true)
          return
        }
        if (m) {
          m.setLocalVideoPreviewImageUri(uri)
        } else {
          setLocalThumb(uri)
        }
      } catch {
        setUseVideoPoster(true)
      }
    }

    const onSelectImage = () => {
      if (
        ![MessageStatus.SENT, MessageStatus.DELIVERED, MessageStatus.READ].includes(message.status)
      )
        return

      navigation.navigate("ImageGallery", {
        images: [
          {
            type: MessageTypes.VIDEO,
            message_id: message.message_id,
            attach_path: message.attach_path,
            content: message.content,
            key: buildGalleryImageKey({
              message_id: message.message_id,
              attach_path: message.attach_path,
              content: message.content,
            }),
          },
        ],
        index: 0,
      })
    }

    const handleOnLongPress = () => {
      if (setPopupOptionListModal) {
        hapticTrigger()
        setPopupOptionListModal({ visible: true, message })
      }
    }

    const frameStyle = {
      width: displayW,
      height: displayH,
      maxWidth: maxW,
    }

    const onLoad = ({
      nativeEvent: { width: iw, height: ih },
    }: {
      nativeEvent: { width: number; height: number }
    }) => {
      if (iw > 0 && ih > 0) {
        setIntrinsic({ w: iw, h: ih })
      }
    }

    useEffect(() => {
      if (displayPreview || !video) return
      void generateThumbnail()
    }, [displayPreview, video, message.message_id])

    return (
      <VideoContainer>
        <VideoMessageTouchableWrap
          style={frameStyle}
          onLongPress={handleOnLongPress}
          onClick={onSelectImage}
          onStartShouldSetResponderCapture={() => true}
        >
          {!message.isUploading && <Progress progress={progress} inProgress={loading} size={50} />}
          {useVideoPoster && !displayPreview ? (
            <VideoPoster
              src={video}
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={(e) => {
                const el = e.currentTarget
                if (el.videoWidth > 0 && el.videoHeight > 0) {
                  setIntrinsic({ w: el.videoWidth, h: el.videoHeight })
                }
              }}
            />
          ) : (
            <FastImage
              style={{ width: "100%", height: "100%" }}
              src={displayPreview || placeholder}
              resizeMode={FastImage.resizeMode.cover}
              onProgress={onProgress}
              onError={onError}
              onLoad={onLoad}
              onLoadEnd={onLoadEnd}
              onLoadStart={onLoadStart}
            />
          )}
          <VideoOverlay onClick={onSelectImage}>
            {MessageStatus.UPLOADING && (
              <Progress
                progress={message.progress}
                inProgress={message.status === MessageStatus.UPLOADING}
                size={50}
              />
            )}
            {message.status !== MessageStatus.UPLOADING && !displayPreview && !useVideoPoster && (
              <Loading size="large"></Loading>
            )}
            {message.status !== MessageStatus.UPLOADING && (displayPreview || useVideoPoster) && (
              <PlayButton aria-hidden />
            )}
          </VideoOverlay>
        </VideoMessageTouchableWrap>
        <TimeWrap byMe={message.byMe}>
          <MessageTime time={message.created} />
        </TimeWrap>
      </VideoContainer>
    )
  },
)
export const VideoMessageWithContainer: React.FC<VideoMessageProps> = (props) => {
  return (
    <Container>
      <VideoMessage {...props} />
    </Container>
  )
}
export const WrappedVideoMessage: React.FC<MessageProps & VideoMessageProps> = (props) => {
  return props.message.byMe ? (
    <MessageSent {...props} Component={VideoMessageWithContainer} isNoBg={true} />
  ) : (
    <MessageReceived {...props} Component={VideoMessageWithContainer} isNoBg={true} />
  )
}
export default WrappedVideoMessage
